/**
 * TokenManager - Centralized token management utility
 *
 * Handles all token operations including:
 * - Token storage and retrieval
 * - Expiration checking
 * - Automatic token refresh
 */

interface JWTPayload {
  sub: string;
  exp: number;
  iat: number;
  UserID: string;
  Version: number;
}

class TokenManager {
  private static instance: TokenManager;
  private apiBaseUrl: string;
  private refreshPromise: Promise<string> | null = null;
  private tokenExpiresAt: number | null = null;

  private constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * Get current access token from storage
   */
  public getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Get refresh token from storage
   */
  public getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  /**
   * Store access token and cache expiry time
   */
  public setToken(token: string): void {
    localStorage.setItem('token', token);

    // Decode token once and cache expiry time
    const payload = this.decodeToken(token);
    if (payload && payload.exp) {
      this.tokenExpiresAt = payload.exp * 1000; // Convert to milliseconds
    } else {
      this.tokenExpiresAt = null;
    }
  }

  /**
   * Store refresh token
   */
  public setRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  }

  /**
   * Clear all tokens from storage
   */
  public clearTokens(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    this.tokenExpiresAt = null;
  }

  /**
   * Decode JWT token payload
   */
  private decodeToken(token: string): JWTPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (err) {
      console.error('Failed to decode JWT token:', err);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  public isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) {
      return true;
    }

    const expiresAt = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    return now >= expiresAt;
  }

  /**
   * Check if token is expiring soon (within threshold)
   * @param token JWT token string
   * @param thresholdMs Time threshold in milliseconds (default: 60 seconds)
   */
  public isTokenExpiringSoon(token: string, thresholdMs: number = 60000): boolean {
    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) {
      return true;
    }

    const expiresAt = payload.exp * 1000;
    const now = Date.now();
    return (expiresAt - now) < thresholdMs;
  }

  /**
   * Check if the stored token needs to be refreshed
   * Uses cached expiry time for O(1) performance
   * @param thresholdMs Time threshold in milliseconds (default: 30 seconds)
   * @returns true if token should be refreshed, false otherwise
   */
  public needsRefresh(thresholdMs: number = 30000): boolean {
    // If no cached expiry, try to initialize it from current token
    if (!this.tokenExpiresAt) {
      const token = this.getToken();
      if (!token) {
        return false; // No token at all
      }

      // Decode token and cache expiry
      const payload = this.decodeToken(token);
      if (payload && payload.exp) {
        this.tokenExpiresAt = payload.exp * 1000;
      } else {
        return false; // Invalid token, can't determine expiry
      }
    }

    const now = Date.now();
    return (this.tokenExpiresAt - now) < thresholdMs;
  }

  /**
   * Refresh the access token using the refresh token
   * Uses a singleton pattern to prevent multiple simultaneous refresh requests
   */
  public async refreshToken(): Promise<string> {
    // If a refresh is already in progress, return that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start new refresh
    this.refreshPromise = this._doRefresh();

    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      // Clear the promise when done (success or failure)
      this.refreshPromise = null;
    }
  }

  /**
   * Internal method to perform the actual token refresh
   */
  private async _doRefresh(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${this.apiBaseUrl}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Refresh token is invalid, clear all tokens
        this.clearTokens();
        throw new Error('Refresh token invalid');
      }
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data = await response.json();
    const newToken = data.access_token;

    if (!newToken) {
      throw new Error('No access token in refresh response');
    }

    // Store the new token
    this.setToken(newToken);

    return newToken;
  }

  /**
   * Get a valid token, refreshing automatically if needed
   * This is the main method that consumers should use
   *
   * @param forceRefresh Force a token refresh even if not expired
   * @returns Promise<string | null> Valid access token, or null if not logged in
   * @throws Error if refresh fails
   */
  public async getValidToken(forceRefresh: boolean = false): Promise<string | null> {
    const token = this.getToken();

    if (!token) {
      return null; // No token available - user not logged in
    }

    // If force refresh requested, refresh immediately
    if (forceRefresh) {
      return await this.refreshToken();
    }

    // Check if token is expired or expiring soon
    if (this.isTokenExpired(token) || this.isTokenExpiringSoon(token, 60000)) {
      return await this.refreshToken();
    }

    // Token is still valid
    return token;
  }

  /**
   * Handle authentication errors by redirecting to login
   * Prevents navigation loop if already on login page
   */
  public handleAuthError(): void {
    this.clearTokens();

    // Don't navigate if already on login page - prevents infinite loop
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
}

// Export singleton instance
export const tokenManager = TokenManager.getInstance();
